// src/features/Planning/components/Step16WallDimentions.jsx
// Heights stored as separate feet + inches fields
import {
  VStack,
  Text,
  Flex,
  Button,
  Box,
  Input,
  Image,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  HStack,
} from "@chakra-ui/react";
import { FiPlay } from "react-icons/fi";
import { useState, useEffect } from "react";
import verticalbuildingsectionimg from "../../../../assets/Planning/WallDimentions/verticalbuildingsectionimg.png";

import PlinthlevelVideo from "../../../../assets/Planning/WallDimentions/infovideo/Plinthlevel.mp4";
import LinthandStillVideo from "../../../../assets/Planning/WallDimentions/infovideo/Still.mp4";
import FloorHeightVideo from "../../../../assets/Planning/WallDimentions/infovideo/FloorHeight.mp4";

const FIELDS = [
  {
    label: "Plinth Level",
    feetKey: "plinthLevelFeet",
    inchesKey: "plinthLevelInches",
    prefix: "plinthLevel",
    videoUrl: PlinthlevelVideo,
    suggestionFeet: "2",
    suggestionInches: "0",
    hint: "Height of floor from natural ground level",
  },
  {
    label: "Lintel Level",
    feetKey: "lintelLevelFeet",
    inchesKey: "lintelLevelInches",
    prefix: "lintelLevel",
    videoUrl: LinthandStillVideo,
    suggestionFeet: "7",
    suggestionInches: "0",
    hint: "Height of top of doors/windows from floor",
  },
  {
    label: "Sill Level",
    feetKey: "sillLevelFeet",
    inchesKey: "sillLevelInches",
    prefix: "sillLevel",
    videoUrl: LinthandStillVideo,
    suggestionFeet: "3",
    suggestionInches: "0",
    hint: "Height of bottom of windows from floor",
  },
  {
    label: "Floor Height (Slab)",
    feetKey: "slabHeightFeet",
    inchesKey: "slabHeightInches",
    prefix: "slabHeight",
    videoUrl: FloorHeightVideo,
    suggestionFeet: "11",
    suggestionInches: "0",
    hint: "Total floor to floor height including slab thickness",
  },
];

// FtInRow — component ke bahar define karo (focus loss fix)
function FtInRow({ label, field, formData, onFeetChange, onInchesChange, onSuggest, onVideoOpen }) {
  const feetVal   = formData?.[field.feetKey]   ?? "";
  const inchesVal = formData?.[field.inchesKey] ?? "0";

  const inputStyles = {
    h: "36px",
    borderRadius: "7px",
    fontSize: "14px",
    textAlign: "center",
    type: "text",
    inputMode: "numeric",
    fontWeight: "500",
    borderColor: "gray.300",
    _hover: { borderColor: "cyan.400" },
    _focus: {
      borderColor: "cyan.500",
      boxShadow: "0 0 0 1px var(--chakra-colors-cyan-500)",
    },
  };

  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="10px"
      p={3}
    >
      <Flex justify="space-between" align="center" mb={1}>
        <Text fontSize="13px" fontWeight="600" color="gray.700">
          {label}
        </Text>
        <HStack spacing={1}>
          {/* Prominent Recommended Button with pulse animation */}
          <Box
            as="button"
            onClick={onSuggest}
            display="flex"
            flexDirection="column"
            alignItems="center"
            cursor="pointer"
            border="1.5px dashed"
            borderColor="cyan.400"
            borderRadius="8px"
            px={2}
            py="3px"
            bg="cyan.50"
            _hover={{ bg: "cyan.100", borderColor: "cyan.500", transform: "scale(1.03)" }}
            _active={{ bg: "cyan.200", transform: "scale(0.98)" }}
            transition="all 0.15s ease"
            title="Apply recommended value"
            sx={{
              animation: "pulse-border 2s infinite",
              "@keyframes pulse-border": {
                "0%, 100%": { boxShadow: "0 0 0 0px rgba(0,188,212,0.25)" },
                "50%": { boxShadow: "0 0 0 4px rgba(0,188,212,0.18)" },
              },
            }}
          >
            <Text fontSize="9px" color="cyan.500" fontWeight="700" letterSpacing="0.5px" lineHeight="1.2">
              👆 TAP TO APPLY
            </Text>
            <Text fontSize="11px" color="cyan.700" fontWeight="700" lineHeight="1.3">
              ✓ {field.suggestionFeet}'{field.suggestionInches}"
            </Text>
            <Text fontSize="9px" color="cyan.500" fontWeight="500" lineHeight="1.2">
              Recommended
            </Text>
          </Box>

          {/* Play Button */}
          <IconButton
            size="md"
            variant="solid"
            bg="cyan.500"
            color="white"
            _hover={{ bg: "cyan.600" }}
            _active={{ bg: "cyan.700" }}
            icon={<FiPlay size={18} />}
            onClick={onVideoOpen}
            aria-label="Watch reference video"
            borderRadius="full"
            boxShadow="md"
            title="Watch explanatory video"
          />
        </HStack>
      </Flex>
      {/* Bold hint for "Height of floor from natural ground level", normal for others */}
      <Text
        fontSize="11px"
        color="gray.500"
        mb={2}
        fontWeight={field.hint === "Height of floor from natural ground level" ? "700" : "400"}
      >
        {field.hint}
      </Text>
      <Flex gap={3}>
        <Box flex="1">
          <Text fontSize="11px" color="gray.400" mb={1} textAlign="center">Feet</Text>
          <Input
            {...inputStyles}
            value={feetVal}
            onChange={(e) => {
              const v = e.target.value;
              if (v !== "" && !/^\d*$/.test(v)) return;
              onFeetChange(field.feetKey, v);
            }}
            placeholder="e.g. 7"
          />
        </Box>
        <Box flex="1">
          <Text fontSize="11px" color="gray.400" mb={1} textAlign="center">Inches (0–11)</Text>
          <Input
            {...inputStyles}
            value={inchesVal}
            onChange={(e) => {
              const v = e.target.value;
              if (v !== "" && (!/^\d*$/.test(v) || parseInt(v) > 11)) return;
              onInchesChange(field.inchesKey, v);
            }}
            placeholder="0"
          />
        </Box>
      </Flex>
    </Box>
  );
}

export default function Step16WallDimentions({
  formData,
  setFormData,
  onNext,
  onBack,
  isLastStep,
  onSubmit,
  viewMode,
}) {
  const { isOpen: isVideoOpen, onOpen: onVideoOpen, onClose: onVideoClose } = useDisclosure();
  const { isOpen: isImageOpen, onOpen: onImageOpen, onClose: onImageClose } = useDisclosure();
  const [activeVideo, setActiveVideo] = useState(null);

  // Prefill recommended values on first mount if fields are empty
  useEffect(() => {
    const updates = {};
    FIELDS.forEach((field) => {
      if (!formData?.[field.feetKey]) updates[field.feetKey] = field.suggestionFeet;
      if (!formData?.[field.inchesKey]) updates[field.inchesKey] = field.suggestionInches;
    });
    if (Object.keys(updates).length > 0) {
      setFormData({ ...formData, ...updates });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFeetChange = (feetKey, val) => {
    setFormData({ ...formData, [feetKey]: val });
  };

  const handleInchesChange = (inchesKey, val) => {
    setFormData({ ...formData, [inchesKey]: val });
  };

  const applySuggestion = (field) => {
    setFormData({
      ...formData,
      [field.feetKey]:   field.suggestionFeet,
      [field.inchesKey]: field.suggestionInches,
    });
  };

  const openVideo = (url) => {
    setActiveVideo(url);
    onVideoOpen();
  };

  const handleNext = () => {
    if (isLastStep) onSubmit();
    else onNext();
  };

  return (
    <Box
      h="600px"
      w="100%"
      maxW="400px"
      mx="auto"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      px={4}
      py={6}
    >
      <Box
        flex="1"
        overflowY="auto"
        pr={1}
        css={{
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-thumb": { background: "#cbd5e0", borderRadius: "8px" },
          "& *": { pointerEvents: viewMode ? "none" : "auto" },
        }}
      >
        <Flex align="center" justify="space-between" mb={3}>
        <Text
            fontSize="17px"
            fontWeight="600"
            color="gray.800"
            textAlign="center"
          >
            Building Heights
          </Text>
          <IconButton
            size="sm"
            variant="ghost"
            icon={<Image src={verticalbuildingsectionimg} boxSize="28px" objectFit="contain" />}
            onClick={onImageOpen}
            aria-label="View diagram"
          />
        </Flex>

        <VStack spacing={3} align="stretch">
          {FIELDS.map((field) => (
            <FtInRow
              key={field.prefix}
              label={field.label}
              field={field}
              formData={formData}
              onFeetChange={handleFeetChange}
              onInchesChange={handleInchesChange}
              onSuggest={() => applySuggestion(field)}
              onVideoOpen={() => openVideo(field.videoUrl)}
            />
          ))}
        </VStack>
      </Box>

      {/* Video Modal */}
      <Modal isOpen={isVideoOpen} onClose={onVideoClose} isCentered size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="14px" pb={2}>Reference Video</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4}>
            {activeVideo && (
              <video
                src={activeVideo}
                controls
                autoPlay
                style={{ width: "100%", borderRadius: "8px" }}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Diagram Modal */}
      <Modal isOpen={isImageOpen} onClose={onImageClose} isCentered size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="14px" pb={2}>Building Section Diagram</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4}>
            <Image src={verticalbuildingsectionimg} w="100%" objectFit="contain" />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Navigation */}
      <Flex justify="space-between" gap={3} mt={3} flexShrink={0}>
        <Button
          onClick={onBack}
          variant="solid"
          bg="white"
          color="cyan.600"
          border="1px solid"
          borderColor="cyan.500"
          borderRadius="6px"
          minW="100px"
          h="40px"
          fontWeight="500"
          fontSize="14px"
          _hover={{ bg: "cyan.50" }}
        >
          ← Previous
        </Button>
        <Button
          onClick={handleNext}
          variant="solid"
          bg="cyan.500"
          color="white"
          borderRadius="6px"
          minW="100px"
          h="40px"
          fontWeight="500"
          fontSize="14px"
          ml="auto"
          _hover={{ bg: "cyan.600" }}
        >
          {isLastStep ? "Submit" : "Next →"}
        </Button>
      </Flex>
    </Box>
  );
}